import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { TextBoxComponent, NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { useTime } from '../../hooks/useTime';
import { useStore } from '../../../store';
import { FeatureControllerService } from '../../api/services/FeatureControllerService';
import { ProductionScheduleControllerService } from '../../api/services/ProductionScheduleControllerService';
import { CalibrationControllerService } from '../../api/services/CalibrationControllerService';

const DebugTestFunctionsPage = () => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const logout = useStore((state) => state.logout);
  const {
    currentTime,
    timeInfo,
    isLoading,
    setMockTime,
    resetMockTime,
    advanceTime,
    refreshTime,
    isTimeControlAvailable,
  } = useTime();

  const [timeInput, setTimeInput] = useState('');
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  /** After a time change: tokens are invalid, the user has to sign in again. */
  const afterTimeChange = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  /** Max. days into the future for the time control (JWT validity). */
  const MAX_DAYS_FORWARD = 6;
  const MS_PER_DAY = 24 * 60 * 60 * 1000;

  /** Real system time (for validation; fallback: the client clock). */
  const getRealTime = (): Date =>
    timeInfo?.realTime ? new Date(timeInfo.realTime) : new Date();

  /** Parses DD.MM.YYYY HH:mm:ss into an ISO string for the API (date/time as local time). Only a dot as the date separator. */
  const parseLocaleTimeToIso = (input: string): string => {
    const trimmed = input.trim();
    const [datePart, timePart] = trimmed.split(/\s+/);
    if (!datePart || !timePart) throw new Error('Format: DD.MM.YYYY HH:mm:ss');
    if (!/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(datePart)) throw new Error('Datum im Format DD.MM.YYYY eingeben (z. B. 17.02.2026)');
    const [day, month, year] = datePart.split('.').map((s) => parseInt(s, 10));
    const timeParts = timePart.split(':').map((s) => parseInt(s, 10) || 0);
    const h = timeParts[0] ?? 0;
    const m = timeParts[1] ?? 0;
    const s = timeParts[2] ?? 0;
    if (!year || !month || !day) throw new Error('Ungültiges Datum');
    const date = new Date(year, month - 1, day, h, m, s);
    if (Number.isNaN(date.getTime())) throw new Error('Ungültiges Datum/Zeit');
    return date.toISOString();
  };

  const handleSetTime = async () => {
    if (!timeInput.trim()) return;
    try {
      const isoTime = parseLocaleTimeToIso(timeInput);
      const targetTime = new Date(isoTime);
      const realTime = getRealTime();
      const maxTime = new Date(realTime.getTime() + MAX_DAYS_FORWARD * MS_PER_DAY);

      if (targetTime.getTime() < realTime.getTime()) {
        alert(
          'Zeit darf nicht in die Vergangenheit gesetzt werden (Token-Sicherheit). Nur Vorwärts bis max. ' +
            MAX_DAYS_FORWARD +
            ' Tage erlaubt.'
        );
        return;
      }
      if (targetTime.getTime() > maxTime.getTime()) {
        alert(
          'Zeit darf maximal ' +
            MAX_DAYS_FORWARD +
            ' Tage in die Zukunft gesetzt werden (JWT-Validität).'
        );
        return;
      }

      await setMockTime(isoTime);
      setTimeInput('');
      await afterTimeChange();
    } catch (error) {
      alert('Zeit setzen fehlgeschlagen: ' + (error as Error).message);
    }
  };

  const handleAdvance = async () => {
    try {
      const advanceMs =
        hours * 3600000 + minutes * 60000 + seconds * 1000;
      const newTime = new Date(currentTime.getTime() + advanceMs);
      const realTime = getRealTime();
      const maxTime = new Date(realTime.getTime() + MAX_DAYS_FORWARD * MS_PER_DAY);

      if (advanceMs < 0) {
        alert('Zeit darf nur vorwärts gedreht werden (nicht in die Vergangenheit).');
        return;
      }
      if (newTime.getTime() > maxTime.getTime()) {
        alert(
          'Nach dem Vorrücken darf die Zeit maximal ' +
            MAX_DAYS_FORWARD +
            ' Tage vor der echten Systemzeit liegen (JWT-Validität). Bitte kleinere Werte eingeben.'
        );
        return;
      }

      await advanceTime(hours, minutes, seconds);
      await afterTimeChange();
    } catch (error) {
      alert('Zeit vorrücken fehlgeschlagen: ' + (error as Error).message);
    }
  };

  const handleResetToSystemTime = async () => {
    try {
      await resetMockTime();
      await afterTimeChange();
    } catch (error) {
      alert('Zurücksetzen fehlgeschlagen: ' + (error as Error).message);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-lg font-bold mb-4">
        {t('routes.debugTestFunctions')}
      </h2>

      {/* Time Control Section (DEV/Stage) */}
      <div className="mb-8">
        <h2 className="text-base font-semibold mb-4">Zeitsteuerung (DEV/Stage)</h2>

        {isTimeControlAvailable ? (
          <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
            <p className="mb-4 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
              Nach dem Setzen, Vorrücken oder Zurücksetzen der Zeit werden alle Tokens ungültig und Sie werden abgemeldet. Bitte melden Sie sich danach erneut an.
            </p>
            <p className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
              Aus Sicherheitsgründen (JWT) darf die Zeit nur in die Zukunft gedreht werden und maximal {MAX_DAYS_FORWARD} Tage vor der echten Systemzeit liegen.
            </p>
            {/* Current Time Display */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <strong>Aktuelle Zeit:</strong>
                <span className="text-base">
                  {timeInfo != null
                    ? (timeInfo.localDateTimeServer ?? currentTime.toLocaleString('de-DE'))
                    : isLoading
                      ? 'Wird geladen…'
                      : 'Backend nicht erreichbar – Backend mit Profil dev oder stage starten, dann „Aktualisieren“ klicken.'}
                </span>
                {timeInfo?.serverZone != null && (
                  <span className="text-gray-500 text-sm">({timeInfo.serverZone})</span>
                )}
                {timeInfo?.isMocked && (
                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm font-semibold">
                    (MOCKED)
                  </span>
                )}
              </div>

              {timeInfo?.isMocked && (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                  <small className="text-gray-600">
                    Offset: {timeInfo.offsetMillis}ms | Real Time:{' '}
                    {new Date(timeInfo.realTime).toLocaleString('de-DE')}
                  </small>
                </div>
              )}
            </div>

            {/* Set Time */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zeit setzen:
              </label>
              <div className="flex gap-2 items-center flex-wrap">
                <TextBoxComponent
                  value={timeInput}
                  onChange={(e: { value: string }) => setTimeInput(e.value ?? '')}
                  placeholder="17.02.2026 16:04:00"
                  floatLabelType="Never"
                  cssClass="flex-1 min-w-[200px]"
                />
                <ButtonComponent
                  onClick={handleSetTime}
                  disabled={isLoading || !timeInput.trim()}
                  cssClass="e-primary"
                >
                  Setzen
                </ButtonComponent>
              </div>
              <small className="text-gray-500 mt-1 block">
                Format: DD.MM.YYYY HH:mm:ss (z. B. 17.02.2026 16:04:00)
              </small>
            </div>

            {/* Advance Time */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zeit vorrücken:
              </label>
              <div className="flex flex-nowrap gap-2 items-center flex-wrap">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-gray-600">Stunde</span>
                  <NumericTextBoxComponent
                    value={hours}
                    change={(e: { value: number | undefined }) => setHours(e.value ?? 0)}
                    format="n0"
                    min={0}
                    max={MAX_DAYS_FORWARD * 24}
                    placeholder="0"
                    floatLabelType="Never"
                    cssClass="w-24"
                  />
                </div>
                <span className="self-end pb-2 text-gray-400">:</span>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-gray-600">Minute</span>
                  <NumericTextBoxComponent
                    value={minutes}
                    change={(e: { value: number | undefined }) => setMinutes(e.value ?? 0)}
                    format="n0"
                    min={0}
                    max={59}
                    placeholder="0"
                    floatLabelType="Never"
                    cssClass="w-24"
                  />
                </div>
                <span className="self-end pb-2 text-gray-400">:</span>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-gray-600">Sekunde</span>
                  <NumericTextBoxComponent
                    value={seconds}
                    change={(e: { value: number | undefined }) => setSeconds(e.value ?? 0)}
                    format="n0"
                    min={0}
                    max={59}
                    placeholder="0"
                    floatLabelType="Never"
                    cssClass="w-24"
                  />
                </div>
                <div className="self-end pb-2">
                  <ButtonComponent
                    onClick={handleAdvance}
                    disabled={isLoading}
                    cssClass="e-success"
                  >
                    Vorrücken
                  </ButtonComponent>
                </div>
              </div>
            </div>

            {/* Reset to system time, Refresh */}
            <div className="flex gap-2 flex-wrap">
              <ButtonComponent
                onClick={handleResetToSystemTime}
                disabled={isLoading}
                cssClass="e-danger"
              >
                Auf Systemzeit zurücksetzen
              </ButtonComponent>
              <ButtonComponent
                onClick={refreshTime}
                disabled={isLoading}
                cssClass="e-outline e-primary"
              >
                Aktualisieren
              </ButtonComponent>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-600">
              Zeitsteuerung ist nur in DEV/Stage verfügbar (Backend mit Profil
              &quot;dev&quot; oder &quot;stage&quot;).
            </p>
          </div>
        )}
      </div>

      {/* Sequence Optimization Calibration */}
      <SequenceCalibrationSection />
    </div>
  );
};

/**
 * Sequence Optimization Calibration section.
 * Only visible when feature flag is enabled (-DenableSequenceOptimization=true).
 */
const SequenceCalibrationSection = () => {
  const [featureEnabled, setFeatureEnabled] = useState<boolean | null>(null);
  const [schedules, setSchedules] = useState<Array<{ uuid: string; scheduleName: string; status: string; createdOn?: string }>>([]);
  const [selectedScheduleUuid, setSelectedScheduleUuid] = useState<string | null>(null);
  const [randomRuns, setRandomRuns] = useState(5);
  const [includeManual, setIncludeManual] = useState(true);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [jobResult, setJobResult] = useState<any>(null);
  const [jobError, setJobError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check feature flag
  useEffect(() => {
    FeatureControllerService.getSequenceOptimization()
      .then((res: any) => setFeatureEnabled(res.enabled === true))
      .catch(() => setFeatureEnabled(false));
  }, []);

  // Load schedules
  useEffect(() => {
    if (!featureEnabled) return;
    ProductionScheduleControllerService.findAll()
      .then((list: any[]) => {
        const sorted = list
          .filter((s: any) => s.status === 'optimized' || s.status === 'new' || s.status === 'failed')
          .sort((a: any, b: any) => (b.plannedStart || '').localeCompare(a.plannedStart || ''));
        setSchedules(sorted.map((s: any) => ({
          uuid: s.uuid,
          scheduleName: s.scheduleName || s.uuid,
          status: s.status,
          createdOn: s.createdOn
        })));
      })
      .catch(() => setSchedules([]));
  }, [featureEnabled]);

  // Poll for job status
  const startPolling = useCallback((id: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const data = await CalibrationControllerService.getStatus(id) as any;
        setJobStatus(data.status);
        if (data.status === 'completed') {
          setJobResult(data.result);
          if (pollRef.current) clearInterval(pollRef.current);
        } else if (data.status === 'failed') {
          setJobError(data.error || 'Unbekannter Fehler');
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {
        // ignore poll errors
      }
    }, 5000);
  }, []);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleStart = async () => {
    if (!selectedScheduleUuid) return;
    setJobId(null);
    setJobStatus(null);
    setJobResult(null);
    setJobError(null);
    try {
      const res = await CalibrationControllerService.runCalibration(
        selectedScheduleUuid, randomRuns, includeManual);
      const data = res as any;
      if (data.jobId) {
        setJobId(data.jobId);
        setJobStatus('running');
        startPolling(data.jobId);
      }
    } catch (err: any) {
      setJobError(err?.message || 'Fehler beim Starten der Kalibrierung');
    }
  };

  if (featureEnabled === null) return null; // loading
  if (!featureEnabled) return null; // feature disabled — section hidden

  const scheduleDropdownData = schedules.map(s => {
    const dateStr = s.createdOn ? new Date(s.createdOn).toLocaleString('de-DE') : '';
    return {
      text: `${s.scheduleName} — ${dateStr} (${s.status})`,
      value: s.uuid
    };
  });

  return (
    <div className="mb-8">
      <h2 className="text-base font-semibold mb-4">Sequenz-Optimierung: Kalibrierung</h2>
      <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
        <p className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
          Startet mehrere Scheduling-Läufe mit verschiedenen Sequenzvarianten (regelbasiert, manuell, zufällig)
          und vergleicht die Ergebnisse. Ergebnisse werden als JSON/CSV unter <code>./debug/calibration-results/</code> gespeichert.
        </p>

        {/* Schedule selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Produktionsplan</label>
          <DropDownListComponent
            dataSource={scheduleDropdownData}
            fields={{ text: 'text', value: 'value' }}
            value={selectedScheduleUuid || ''}
            change={(e: any) => setSelectedScheduleUuid(e.value || null)}
            placeholder="Plan wählen..."
            floatLabelType="Never"
            cssClass="w-full"
          />
        </div>

        {/* Parameters */}
        <div className="flex gap-4 mb-4 items-end flex-wrap">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-gray-600">Zufällige Sequenzen</span>
            <NumericTextBoxComponent
              value={randomRuns}
              change={(e: { value: number | undefined }) => setRandomRuns(e.value ?? 5)}
              format="n0"
              min={0}
              max={50}
              placeholder="5"
              floatLabelType="Never"
              cssClass="w-32"
            />
          </div>
          <label className="flex items-center gap-2 pb-1 cursor-pointer">
            <input
              type="checkbox"
              checked={includeManual}
              onChange={(e) => setIncludeManual(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Manuelle Sequenzen einbeziehen</span>
          </label>
        </div>

        {/* Start button */}
        <div className="mb-4">
          <ButtonComponent
            onClick={handleStart}
            disabled={!selectedScheduleUuid || jobStatus === 'running'}
            cssClass="e-primary"
          >
            {jobStatus === 'running' ? 'Kalibrierung läuft...' : 'Kalibrierung starten'}
          </ButtonComponent>
        </div>

        {/* Status */}
        {jobStatus === 'running' && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
            Kalibrierung läuft (Job: {jobId}). Dies kann 10-15 Minuten dauern...
          </div>
        )}
        {jobStatus === 'failed' && (
          <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            Kalibrierung fehlgeschlagen: {jobError}
          </div>
        )}
        {jobStatus === 'completed' && jobResult && (
          <div className="mt-4">
            <h3 className="text-sm font-semibold mb-2">
              Ergebnis: {jobResult.runs?.length || 0} Varianten, {jobResult.totalOrders} Aufträge
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left border-b">Rang</th>
                    <th className="px-3 py-2 text-left border-b">Variante</th>
                    <th className="px-3 py-2 text-left border-b">Typ</th>
                    <th className="px-3 py-2 text-right border-b">Feasible</th>
                    <th className="px-3 py-2 text-right border-b">Infeasible</th>
                    <th className="px-3 py-2 text-right border-b">Makespan (min)</th>
                    <th className="px-3 py-2 text-right border-b">DueTime Verl.</th>
                    <th className="px-3 py-2 text-right border-b">Laufzeit (s)</th>
                  </tr>
                </thead>
                <tbody>
                  {(jobResult.runs || []).map((run: any, idx: number) => (
                    <tr key={idx} className={idx === 0 ? 'bg-green-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 border-b font-medium">{run.rank}</td>
                      <td className="px-3 py-2 border-b">{run.variantName}</td>
                      <td className="px-3 py-2 border-b text-xs text-gray-500">{run.variantType}</td>
                      <td className="px-3 py-2 border-b text-right">{run.metrics?.feasibleOrders}</td>
                      <td className="px-3 py-2 border-b text-right text-red-600">{run.metrics?.infeasibleOrders || 0}</td>
                      <td className="px-3 py-2 border-b text-right">{run.metrics?.makespanMinutes?.toFixed(1)}</td>
                      <td className="px-3 py-2 border-b text-right">{run.metrics?.dueDateViolations || 0}</td>
                      <td className="px-3 py-2 border-b text-right">{(run.runtimeMs / 1000).toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Detaillierte Ergebnisse: ./debug/calibration-results/{jobResult.calibrationId}.json
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DebugTestFunctionsPage;
